import { Observable, Subject } from 'rxjs';
import { catchError, exhaustMap, map, shareReplay, startWith } from 'rxjs/operators';
import { of } from 'rxjs';

export interface LoadingAction<T = void> {
  /** Call this to trigger the operation (e.g. from a button click) */
  trigger: () => void;
  /** Emits true while the operation is in progress */
  loading$: Observable<boolean>;
  /** Emits the result value on success, null on error */
  result$: Observable<T | null>;
}

/**
 * Creates an RxJS-native loading action with built-in double-click prevention.
 *
 * Uses exhaustMap — new triggers are ignored while the operation is in flight.
 * No boolean flags needed in the component.
 *
 * @example
 * // In the component class:
 * readonly save = loadingAction(() => this.apiService.save(this.data));
 *
 * // In the template:
 * <ng-container *ngIf="{ loading: save.loading$ | async } as vm">
 *   <button mat-raised-button [disabled]="vm.loading" (click)="save.trigger()">
 *     <mat-spinner *ngIf="vm.loading" [diameter]="20"></mat-spinner>
 *     {{ vm.loading ? 'Saving...' : 'Save' }}
 *   </button>
 * </ng-container>
 */
export function loadingAction<T>(operation: () => Observable<T>): LoadingAction<T> {
  const trigger$ = new Subject<void>();

  const shared$ = trigger$.pipe(
    exhaustMap(() =>
      operation().pipe(
        map(result => ({ loading: false, result: result as T | null })),
        startWith({ loading: true, result: null as T | null }),
        catchError(() => of({ loading: false, result: null as T | null }))
      )
    ),
    startWith({ loading: false, result: null as T | null }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  return {
    trigger: () => trigger$.next(),
    loading$: shared$.pipe(map(s => s.loading)),
    result$: shared$.pipe(map(s => s.result)),
  };
}
